import { useCallback, useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { gallery, galleryCategories } from "@/data/content";

export const Gallery = () => {
  const [filter, setFilter] = useState("Vše");
  const [lightbox, setLightbox] = useState(-1);
  const touchX = useRef(null);

  const items = filter === "Vše" ? gallery : gallery.filter((g) => g.category === filter);

  const close = useCallback(() => setLightbox(-1), []);
  const step = useCallback(
    (dir) => setLightbox((i) => (i + dir + items.length) % items.length),
    [items.length]
  );

  useEffect(() => {
    if (lightbox < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, close, step]);

  return (
    <section id="realizace" className="scroll-mt-24 bg-background" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">Realizace</p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            Výsledky naší práce
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8 flex flex-wrap gap-2" data-testid="gallery-filters">
            {galleryCategories.map((c) => (
              <button
                key={c}
                data-testid={`gallery-filter-${c}`}
                onClick={() => {
                  setFilter(c);
                  setLightbox(-1);
                }}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                  filter === c
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-transparent text-muted-foreground hover:border-foreground/40 hover:text-foreground"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        {/* Editorial masonry layout */}
        <div className="mt-10 columns-2 gap-4 lg:columns-3 lg:gap-6" data-testid="gallery-grid">
          {items.map((item, i) => (
            <button
              key={item.src}
              data-testid={`gallery-item-${i}`}
              onClick={() => setLightbox(i)}
              className="group mb-4 block w-full break-inside-avoid text-left lg:mb-6"
            >
              <span className="block overflow-hidden rounded-md">
                <img
                  src={item.src}
                  alt={item.title}
                  loading="lazy"
                  className="w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                />
              </span>
              <span className="mt-2 flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-foreground">{item.title}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{item.category}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox >= 0 && items[lightbox] && (
        <div
          data-testid="lightbox"
          className="fixed inset-0 z-[60] flex flex-col bg-foreground/95 p-4 sm:p-8"
          onClick={close}
          onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
          onTouchEnd={(e) => {
            if (touchX.current == null) return;
            const dx = e.changedTouches[0].clientX - touchX.current;
            if (Math.abs(dx) > 50) step(dx < 0 ? 1 : -1);
            touchX.current = null;
          }}
        >
          <div className="flex items-center justify-between text-background">
            <p className="text-sm">
              <span className="font-semibold">{items[lightbox].title}</span>
              <span className="ml-3 opacity-70">{items[lightbox].category}</span>
            </p>
            <button
              data-testid="lightbox-close"
              onClick={close}
              aria-label="Zavřít galerii"
              className="flex h-11 w-11 items-center justify-center rounded-full transition-colors hover:bg-background/10"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="relative flex flex-1 items-center justify-center overflow-hidden">
            <button
              data-testid="lightbox-prev"
              onClick={(e) => {
                e.stopPropagation();
                step(-1);
              }}
              aria-label="Předchozí fotografie"
              className="absolute left-0 z-10 hidden h-12 w-12 items-center justify-center rounded-full text-background transition-colors hover:bg-background/10 sm:flex"
            >
              <ChevronLeft className="h-7 w-7" />
            </button>
            <img
              src={items[lightbox].src}
              alt={items[lightbox].title}
              onClick={(e) => e.stopPropagation()}
              className="max-h-full max-w-full rounded-sm object-contain"
            />
            <button
              data-testid="lightbox-next"
              onClick={(e) => {
                e.stopPropagation();
                step(1);
              }}
              aria-label="Další fotografie"
              className="absolute right-0 z-10 hidden h-12 w-12 items-center justify-center rounded-full text-background transition-colors hover:bg-background/10 sm:flex"
            >
              <ChevronRight className="h-7 w-7" />
            </button>
          </div>
          <p className="pt-3 text-center text-xs text-background/70">
            {items[lightbox].note} · {lightbox + 1} / {items.length}
          </p>
        </div>
      )}
    </section>
  );
};
